import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from '@bonanza-social/i18n';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import {
  selectTokenBalance,
  selectTokenName,
  useSelector,
} from '@bonanza-social/toolkit/state';
import {
  Box,
  Button,
  Image,
  Text,
  Timer,
  useStyle,
} from '@bonanza-social/moonlight';
import {
  selectPrizeWallCampaignPromoImage,
  selectPrizeWallCampaignQuickRulesShown,
} from '../../state/prizeWallCampaign/prizeWallCampaign.slice';
import {
  selectPrizeWallState,
  selectPrizeWallOpenDate,
} from '../../state/prizeWall/prizeWall.slice';
import { PrizeWallCampaignBannerProps } from './PrizeWallCampaignBanner.types';
import PrizeWallTokenIcon from '../PrizeWallTokenIcon/PrizeWallTokenIcon';
import PrizeWallImage from '../common/PrizeWallImage/PrizeWallImage';

function PrizeWallCampaignBanner(props: PrizeWallCampaignBannerProps) {
  const [t] = useTranslation('prizeWall');
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const tokenName = useSelector(selectTokenName);
  const tokenBalance = useSelector(selectTokenBalance);
  const promoImage = useSelector(selectPrizeWallCampaignPromoImage);
  const state = useSelector(selectPrizeWallState);
  const openDate = useSelector(selectPrizeWallOpenDate);
  const quickRulesShown = useSelector(selectPrizeWallCampaignQuickRulesShown);
  const style = useStyle(props);

  const onPress = useCallback(() => {
    navigation.navigate('PrizeWallCampaign', {
      screen: quickRulesShown ? 'PrizeWallScreen' : 'PrizeWallQuickRulesScreen',
    });
  }, [quickRulesShown, navigation]);

  return (
    <Pressable style={style} onPress={onPress}>
      <Box
        gap="space-200"
        padding="space-400"
        borderColor="borderGoldSolid"
        borderRadius="radius-400"
        borderWidth="border-width-025"
        overflow="hidden"
      >
        <PrizeWallImage
          {...promoImage}
          contentFit="cover"
          position="absolute"
          inset={0}
        />
        {state === 'Pending' && (
          <Box
            gap="space-050"
            top="space-400"
            right="space-400"
            position="absolute"
            flexDirection="row"
            paddingLeft="space-100"
            paddingRight="space-200"
            borderRadius="radius-full"
            backgroundColor="onLightTertiaryBackground"
          >
            <Image
              name="prizeWall.timer"
              width="space-500"
              height="space-500"
            />
            {openDate && (
              <Timer
                time={openDate}
                unitSize="short"
                displayMode="condensed"
                color="darkSurfacePrimary"
                paddingVertical="space-050"
                variant="caption"
              />
            )}
          </Box>
        )}
        <Box flexDirection="row" gap="space-300">
          <Box
            borderColor="borderSecondary"
            borderWidth="border-width-050"
            borderRadius="radius-full"
            width="space-1400"
            height="space-1400"
            centerContent
          >
            <PrizeWallTokenIcon width="space-800" height="space-800" />
          </Box>
          <Box>
            <Text variant="body-s" color="darkSurfaceSecondary">
              {t('prizeWallBannerTokens', { tokenName })}
            </Text>
            <Text variant="h4" color="darkSurfacePrimary">
              {`${tokenBalance}`}
            </Text>
          </Box>
        </Box>
        <Button
          variant="tertiary"
          locNS="prizeWall"
          locKey="prizeWallBannerCTA"
          rightIcon="arrowForward"
          alignSelf="flex-start"
          pointerEvents="none"
        />
      </Box>
    </Pressable>
  );
}

export default memo(PrizeWallCampaignBanner);
